import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Table,
} from "reactstrap";
import Select from "react-select";
import cogoToast from "cogo-toast";
import { FaSync, FaBuilding, FaSave, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import {
  bulkUpdateStaffDesignationDepartmentApi,
  listDepartmentsApi,
  listStaffDesignationsApi,
} from "../../services/Api";
import { SUCCESS_RESULT } from "../../Constants";

const getDepartmentName = (row) =>
  row?.department_name ||
  row?.department?.department_name ||
  row?.department?.name ||
  "—";

const getDepartmentId = (row) =>
  row?.department_id || row?.department?.id || "";

const getStatusLabel = (row) =>
  Number(row?.status) === SUCCESS_RESULT ? "Active" : String(row?.status ?? "");

const getUpdatedAt = (row) => row?.updated_at || row?.created_at || "";

const SORT_GETTERS = {
  role_name: (row) => (row?.role_name || "").toLowerCase(),
  department: (row) => getDepartmentName(row).toLowerCase(),
  status: (row) => getStatusLabel(row).toLowerCase(),
  updated: (row) => getUpdatedAt(row),
};

const sortableHeaderStyle = {
  cursor: "pointer",
  userSelect: "none",
  whiteSpace: "nowrap",
};

const RoleDepartments = () => {
  const [rows, setRows] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: "role_name",
    direction: "asc",
  });

  useEffect(() => {
    fetchRoles();
    fetchDepartments();
  }, []);

  const fetchRoles = () => {
    setLoading(true);
    listStaffDesignationsApi({})
      .then((res) => {
        setLoading(false);
        if (res.status === 200 && res.data.status === SUCCESS_RESULT) {
          const data = res.data.data;
          setRows(Array.isArray(data) ? data : []);
          setSelectedIds([]);
        } else {
          setRows([]);
          cogoToast.error(res.data?.message || "Failed to load designations", {
            hideAfter: 5,
          });
        }
      })
      .catch((err) => {
        setLoading(false);
        setRows([]);
        cogoToast.error(
          err?.response?.data?.message || "Something went wrong loading designations.",
          { hideAfter: 5 }
        );
      });
  };

  const fetchDepartments = () => {
    setLoadingDepartments(true);
    listDepartmentsApi({})
      .then((res) => {
        setLoadingDepartments(false);
        if (res.status === 200 && res.data.status === SUCCESS_RESULT) {
          const data = res.data.data;
          setDepartments(Array.isArray(data) ? data : []);
        } else {
          setDepartments([]);
          cogoToast.error(res.data?.message || "Failed to load departments", {
            hideAfter: 5,
          });
        }
      })
      .catch((err) => {
        setLoadingDepartments(false);
        setDepartments([]);
        cogoToast.error(
          err?.response?.data?.message || "Something went wrong loading departments.",
          { hideAfter: 5 }
        );
      });
  };

  const departmentOptions = useMemo(
    () =>
      (departments || []).map((d) => ({
        value: d.id,
        label: d.department_name || d.name || d.id,
      })),
    [departments]
  );

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key, direction: "asc" };
    });
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <FaSort className="ms-1 text-muted" style={{ opacity: 0.45 }} />;
    }
    return sortConfig.direction === "asc" ? (
      <FaSortUp className="ms-1" />
    ) : (
      <FaSortDown className="ms-1" />
    );
  };

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let filtered = rows;
    if (q) {
      filtered = rows.filter((row) => {
        const haystack = [
          row.role_name,
          row.id,
          getDepartmentName(row),
          getDepartmentId(row),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      });
    }

    const getter = SORT_GETTERS[sortConfig.key];
    if (!getter) return filtered;

    const sorted = [...filtered].sort((a, b) => {
      const aVal = getter(a);
      const bVal = getter(b);
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [rows, search, sortConfig]);

  const allVisibleSelected =
    visibleRows.length > 0 &&
    visibleRows.every((row) => selectedIds.includes(row.id));

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      const visibleIdSet = new Set(visibleRows.map((r) => r.id));
      setSelectedIds((prev) => prev.filter((id) => !visibleIdSet.has(id)));
      return;
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      visibleRows.forEach((row) => next.add(row.id));
      return Array.from(next);
    });
  };

  const toggleRow = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const openUpdateModal = () => {
    if (selectedIds.length === 0) {
      cogoToast.error("Select at least one role first.", { hideAfter: 3 });
      return;
    }
    setSelectedDepartment(null);
    setModalOpen(true);
  };

  const closeUpdateModal = () => {
    if (saving) return;
    setModalOpen(false);
    setSelectedDepartment(null);
  };

  const saveDepartment = () => {
    if (!selectedDepartment?.value) {
      cogoToast.error("Please select a department.", { hideAfter: 3 });
      return;
    }
    if (selectedIds.length === 0) {
      cogoToast.error("Select at least one role first.", { hideAfter: 3 });
      return;
    }

    setSaving(true);
    bulkUpdateStaffDesignationDepartmentApi({
      role_ids: selectedIds,
      department_id: selectedDepartment.value,
    })
      .then((res) => {
        setSaving(false);
        if (res.status === 200 && res.data.status === SUCCESS_RESULT) {
          cogoToast.success(
            res.data.message || "Departments updated successfully.",
            { hideAfter: 3 }
          );
          setModalOpen(false);
          setSelectedDepartment(null);
          fetchRoles();
        } else {
          cogoToast.error(res.data?.message || "Failed to update departments", {
            hideAfter: 5,
          });
        }
      })
      .catch((err) => {
        setSaving(false);
        cogoToast.error(
          err?.response?.data?.message ||
            "Something went wrong updating departments.",
          { hideAfter: 5 }
        );
      });
  };

  return (
    <div className="container-fluid px-3 px-lg-4 mt-4 mb-5">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h2 className="page-title mb-1">Role Departments</h2>
          <div className="text-muted">
            HRM · Setup · Assign departments to staff designations
          </div>
        </div>
        <Button color="secondary" outline onClick={fetchRoles} disabled={loading}>
          <FaSync className="me-1" /> Refresh
        </Button>
      </div>

      <Card className="shadow-sm mb-3">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <Input
                style={{ maxWidth: 280 }}
                placeholder="Search role or department…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <span className="text-muted small">
                {loading
                  ? "Loading…"
                  : `${visibleRows.length} role(s) · ${selectedIds.length} selected`}
              </span>
            </div>
            <Button
              color="primary"
              type="button"
              onClick={openUpdateModal}
              disabled={selectedIds.length === 0 || saving}
            >
              <FaBuilding className="me-1" /> Update department
            </Button>
          </div>
        </CardBody>
      </Card>

      <Card className="shadow-sm">
        <CardBody>
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : visibleRows.length === 0 ? (
            <div className="text-center text-muted py-5">
              No designations found.
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="align-middle mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 48 }}>
                      <Input
                        type="checkbox"
                        checked={allVisibleSelected}
                        onChange={toggleSelectAllVisible}
                        aria-label="Select all visible roles"
                      />
                    </th>
                    <th
                      style={sortableHeaderStyle}
                      onClick={() => handleSort("role_name")}
                    >
                      Role name
                      {renderSortIcon("role_name")}
                    </th>
                    <th
                      style={sortableHeaderStyle}
                      onClick={() => handleSort("department")}
                    >
                      Department
                      {renderSortIcon("department")}
                    </th>
                    <th
                      style={sortableHeaderStyle}
                      onClick={() => handleSort("status")}
                    >
                      Status
                      {renderSortIcon("status")}
                    </th>
                    <th
                      style={sortableHeaderStyle}
                      onClick={() => handleSort("updated")}
                    >
                      Updated
                      {renderSortIcon("updated")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <Input
                          type="checkbox"
                          checked={selectedIds.includes(row.id)}
                          onChange={() => toggleRow(row.id)}
                          aria-label={`Select ${row.role_name || row.id}`}
                        />
                      </td>
                      <td>
                        <div className="fw-semibold">{row.role_name || "—"}</div>
                        <small className="text-muted">{row.id}</small>
                      </td>
                      <td>{getDepartmentName(row)}</td>
                      <td>
                        <Badge
                          color={
                            Number(row.status) === SUCCESS_RESULT
                              ? "success"
                              : "secondary"
                          }
                        >
                          {Number(row.status) === SUCCESS_RESULT
                            ? "Active"
                            : row.status ?? "—"}
                        </Badge>
                      </td>
                      <td>
                        <small>{row.updated_at || row.created_at || "—"}</small>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      <Modal isOpen={modalOpen} toggle={closeUpdateModal} centered>
        <ModalHeader toggle={closeUpdateModal}>Update department</ModalHeader>
        <ModalBody>
          <p className="mb-3">
            Assign a department to <strong>{selectedIds.length}</strong> selected
            role(s).
          </p>
          <FormGroup>
            <Label>Department</Label>
            <Select
              classNamePrefix="react-select"
              isClearable
              isLoading={loadingDepartments}
              options={departmentOptions}
              value={selectedDepartment}
              onChange={setSelectedDepartment}
              placeholder={
                loadingDepartments ? "Loading departments..." : "Select department"
              }
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={closeUpdateModal} disabled={saving}>
            Cancel
          </Button>
          <Button color="primary" onClick={saveDepartment} disabled={saving}>
            {saving ? (
              <>
                <Spinner size="sm" className="me-2" /> Saving…
              </>
            ) : (
              <>
                <FaSave className="me-1" /> Save
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default RoleDepartments;
